/* START_MODULE_CONTRACT
 * PURPOSE: Frontend UI module: ProjectOverheadSection.
 * SCOPE: React component or page.
 * DEPENDS: M-FE-API, M-FE-AUTH
 * LINKS: M-FE-PROJECTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectOverheadSection - Frontend UI module: ProjectOverheadSection.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты функций, парный END_METHOD, блоки загрузки/логи.
 * END_CHANGE_SUMMARY
 */

import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  createProjectOverhead,
  deleteProjectOverhead,
  getProjectOverheads,
  updateProjectOverhead,
} from '../../../api/projectOverheadApi';
import { getProjectRoles } from '../../../api/projectRoleApi';
import ProjectOverhead from '../../../models/ProjectOverhead';
import ProjectRole from '../../../models/ProjectRole';
import OverheadEntryDialog from './OverheadEntryDialog';
import DeleteOverheadDialog from './DeleteOverheadDialog';

/* START_METHOD_ProjectOverheadSection */
/* START_CONTRACT: ProjectOverheadSection
 * PURPOSE: Секция управления накладными расходами проекта (список, CRUD, диалоги).
 * INPUTS: { projectId: number, disabled: boolean, active: boolean }.
 * OUTPUTS: { JSX.Element - секция оверхеда }.
 * SIDE_EFFECTS: HTTP get/create/update/delete overhead и roles; локальный state.
 * LINKS: M-FE-PROJECTS, M-FE-API
 * END_CONTRACT: ProjectOverheadSection
 */
export default function ProjectOverheadSection({ projectId, disabled, active }) {
  const [overheads, setOverheads] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [entryOpen, setEntryOpen] = useState(false);
  const [editingOverhead, setEditingOverhead] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingOverhead, setDeletingOverhead] = useState(null);

  /* START_METHOD_loadData */
  /* START_CONTRACT: loadData
   * PURPOSE: Загрузить оверхеды и роли проекта параллельно; заполнить state.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP getProjectOverheads/getProjectRoles; setOverheads/setRoles/setError/setLoading.
   * LINKS: M-FE-API
   * END_CONTRACT: loadData
   */
  const loadData = useCallback(async () => {
    if (!projectId || !active) return;
    setLoading(true);
    setError(null);
    try {
      const [overheadsResponse, rolesResponse] = await Promise.all([
        getProjectOverheads(projectId),
        getProjectRoles(projectId),
      ]);
      /* START_BLOCK_LOAD_OK */
      setOverheads(overheadsResponse.data.map(ProjectOverhead.fromResponse));
      setRoles(rolesResponse.data.map(ProjectRole.fromResponse));
      /* END_BLOCK_LOAD_OK */
    } catch (err) {
      /* START_BLOCK_LOAD_FAIL */
      console.warn('[ProjectOverheadSection][loadData][LOAD_FAIL] overhead load failed', {
        status: err.response?.status,
      });
      setError(err.response?.data?.message ?? 'Не удалось загрузить оверхед');
      /* END_BLOCK_LOAD_FAIL */
    } finally {
      setLoading(false);
    }
  }, [projectId, active]);
  /* END_METHOD_loadData */

  useEffect(() => {
    loadData();
  }, [loadData]);

  /* START_METHOD_handleCreate */
  /* START_CONTRACT: handleCreate
   * PURPOSE: Создать оверхед и добавить его в список.
   * INPUTS: { payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP createProjectOverhead; setOverheads.
   * LINKS: M-FE-API
   * END_CONTRACT: handleCreate
   */
  const handleCreate = async (payload) => {
    const response = await createProjectOverhead(projectId, payload);
    setOverheads((prev) => [...prev, ProjectOverhead.fromResponse(response.data)]);
  };
  /* END_METHOD_handleCreate */

  /* START_METHOD_handleUpdate */
  /* START_CONTRACT: handleUpdate
   * PURPOSE: Обновить редактируемый оверхед и заменить его в списке.
   * INPUTS: { payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP updateProjectOverhead; setOverheads.
   * LINKS: M-FE-API
   * END_CONTRACT: handleUpdate
   */
  const handleUpdate = async (payload) => {
    const response = await updateProjectOverhead(projectId, editingOverhead.id, payload);
    const updated = ProjectOverhead.fromResponse(response.data);
    setOverheads((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
  };
  /* END_METHOD_handleUpdate */

  /* START_METHOD_handleDelete */
  /* START_CONTRACT: handleDelete
   * PURPOSE: Удалить выбранный оверхед и убрать его из списка.
   * INPUTS: {}.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: HTTP deleteProjectOverhead; setOverheads.
   * LINKS: M-FE-API
   * END_CONTRACT: handleDelete
   */
  const handleDelete = async () => {
    await deleteProjectOverhead(projectId, deletingOverhead.id);
    setOverheads((prev) => prev.filter((item) => item.id !== deletingOverhead.id));
  };
  /* END_METHOD_handleDelete */

  /* START_METHOD_handleEntrySubmit */
  /* START_CONTRACT: handleEntrySubmit
   * PURPOSE: Диспетчеризация: обновить существующий оверхед или создать новый.
   * INPUTS: { payload: object }.
   * OUTPUTS: { Promise<void> }.
   * SIDE_EFFECTS: делегирует handleUpdate/handleCreate.
   * LINKS: M-FE-PROJECTS
   * END_CONTRACT: handleEntrySubmit
   */
  const handleEntrySubmit = async (payload) => {
    if (editingOverhead) {
      /* START_BLOCK_SUBMIT_EDIT */
      await handleUpdate(payload);
      return;
      /* END_BLOCK_SUBMIT_EDIT */
    }
    /* START_BLOCK_SUBMIT_CREATE */
    await handleCreate(payload);
    /* END_BLOCK_SUBMIT_CREATE */
  };
  /* END_METHOD_handleEntrySubmit */

  return (
    <Box sx={{ mt: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2">Накладное время (оверхед)</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingOverhead(null);
            setEntryOpen(true);
          }}
          disabled={disabled || loading || roles.length === 0}
        >
          Добавить
        </Button>
      </Box>

      {roles.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Добавьте роли в проект, чтобы настроить оверхед
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : overheads.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Записи оверхеда не заданы
        </Typography>
      ) : (
        <List dense disablePadding>
          {overheads.map((overhead) => (
            <ListItem
              key={overhead.id}
              secondaryAction={(
                <Box>
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label="Редактировать оверхед"
                    onClick={() => {
                      setEditingOverhead(overhead);
                      setEntryOpen(true);
                    }}
                    disabled={disabled}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    edge="end"
                    size="small"
                    aria-label="Удалить оверхед"
                    onClick={() => {
                      setDeletingOverhead(overhead);
                      setDeleteOpen(true);
                    }}
                    disabled={disabled}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              sx={{ pr: 10 }}
            >
              <ListItemText primary={overhead.getSummaryLabel(roles)} />
            </ListItem>
          ))}
        </List>
      )}

      <OverheadEntryDialog
        open={entryOpen}
        onClose={() => {
          setEntryOpen(false);
          setEditingOverhead(null);
        }}
        onSubmit={handleEntrySubmit}
        overhead={editingOverhead}
        roles={roles}
      />

      <DeleteOverheadDialog
        open={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setDeletingOverhead(null);
        }}
        onConfirm={handleDelete}
        overhead={deletingOverhead}
        roles={roles}
      />
    </Box>
  );
}
/* END_METHOD_ProjectOverheadSection */
